import { DateText } from "@portal/BaseComponents/Date/DateText";
import { DetailsViewLayout } from "@portal/BaseComponents/DetailsView/DetailsView";
import { Divider } from "@portal/BaseComponents/Divider/AntDivider";
import { Flex } from "@portal/BaseComponents/Flex/Flex";
import { Profile } from "@portal/BaseComponents/Profile/Profile";
import { Skeleton } from "@portal/BaseComponents/Skeleton/AntSkeleton";
import { AmountText } from "@portal/BaseComponents/Table/Components/Amount/AmountText";
import { InvoicesColumns } from "@portal/BaseComponents/Table/Components/Invoices/InvoicesColumn";
import { InvoicesText } from "@portal/BaseComponents/Table/Components/Invoices/InvoicesText";
import { mapInvoicesColumnPredicate } from "@portal/BaseComponents/Table/Components/Invoices/predicate";
import { TypographyText } from "@portal/BaseComponents/Typography/AntTypography";
import { PTPDetails } from "@portal/types/entities/ptp";
import { DescriptionsProps } from "antd";
import SkeletonInput from "antd/es/skeleton/Input";
import { filter } from "lodash";
import { useMemo } from "react";

interface PTPDetailsCompProps {
  data?: PTPDetails;
  loading: boolean;
  footer?: React.ReactNode;
}
function PTPDetailsComp(props: PTPDetailsCompProps) {
  const { data, loading } = props;
  function getInvoices(): InvoicesColumns[] {
    if (!data) return [];
    return [data.invoice].map(mapInvoicesColumnPredicate);
  }

  const invoices = useMemo(getInvoices, [data]);

  const CreatedBy = (
    <Skeleton loading={loading} active avatar paragraph={{ rows: 0 }}>
      <Profile
        textStyle={{
          date: {
            fs: "12",
          },
        }}
        dateAndTime={data?.created_by?.created_at}
        firstName={data?.created_by?.first_name ?? ""}
        lastName={data?.created_by?.last_name}
        designation={data?.created_by?.designation}
      />
    </Skeleton>
  );
  const Desc = loading ? (
    <SkeletonInput />
  ) : (
    <TypographyText style={{whiteSpace: 'pre-wrap'}}>{data?.description ?? "-"}</TypographyText>
  );
  const PTPDate =
    !loading && data ? <DateText value={data.pay_date} /> : <SkeletonInput />;
  const PTPAmount =
    data && !loading ? (
      <AmountText
        amount={data.amount}
        currency={data.currency}
        textProps={{
          strong: true,
        }}
      />
    ) : (
      <SkeletonInput />
    );
  const PTPBalanceAmount =
    data && !loading ? (
      <AmountText
        amount={data.balance_amount}
        currency={data.currency}
        textProps={{
          strong: true,
        }}
      />
    ) : (
      <SkeletonInput active />
    );
  const AssociatedInvoice =
    data && !loading ? (
      <>
        {invoices.map((invoice) => {
          return (
            <InvoicesText
              overRideStyle={{ width: "auto" }}
              invoice={invoice}
              key={invoice.id}
              hide={{ amount: false }}
              textStyle={{ amount: { fs: "14" }, title: { fs: "14" } }}
            />
          );
        })}
      </>
    ) : (
      <SkeletonInput active />
    );
  // const PTPOwners = (
  //   <DetailsContent
  //     heading="PTP Owners"
  //     content={
  //       data ? (
  //         <>
  //           {data.owners.map((owner) => {
  //             return (
  //               <Profile
  //                 key={owner.id}
  //                 dateAndTime={owner.created_at}
  //                 firstName={owner.first_name}
  //                 lastName={owner.last_name}
  //                 designation={owner.designation}
  //                 wrapperStyle={{ align: "center" }}
  //               />
  //             );
  //           })}
  //         </>
  //       ) : (
  //         <SkeletonInput active />
  //       )
  //     }
  //   />
  // );
  const TopArea = (
    <Flex vertical space="2" align="center" style={{ width: "100%" }}>
      {CreatedBy}
    </Flex>
  );
  const ClosedDate = (
    <>
      {data ? <DateText value={data.updated_at} showTime /> : <SkeletonInput />}
    </>
  );

  function getItems(): DescriptionsProps["items"] {
    return [
      { key: "DESC", children: Desc, span: 2 },
      { key: "PTPDate", label: "PTP Date", children: PTPDate },
      { key: "PTPAmount", label: "PTP Amount", children: PTPAmount },
      {
        key: "AssociatedInvoice",
        label: "Associated Invoice",
        children: AssociatedInvoice,
      },
      {
        key: "PTPBalanceAmount",
        label: "PTP Balance Amount",
        children: PTPBalanceAmount,
      },
      { key: "ClosedDate", label: "Closed Date", children: ClosedDate },
      // { key: "PTPOwners", label: "PTP Owners", children: PTPOwners },
    ];
  }

  const items = getItems();
  const modified = filter(items, (list) => {
    if (data?.status !== "CLOSED") {
      return list.key !== "ClosedDate";
    }
    return true;
  });
  return (
    <Flex vertical space="24">
      {TopArea}
      <div style={{ width: "100%", paddingInlineStart: "48px" }}>
        <Divider style={{ margin: 0 }} />
      </div>
      <DetailsViewLayout
        items={modified}
        style={{ paddingInlineStart: "48px" }}
      />
      <>{props.footer}</>
    </Flex>
  );
}

export { PTPDetailsComp };
